import { useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabaseClient'
import { validatePassword } from '../lib/passwordValidator'
import AuthLayout from '../components/AuthLayout'
import Input from '../components/Input'
import PasswordStrength from '../components/PasswordStrength'
import { useToast } from '../components/useToast' 

export default function Register() {

  const [form, setForm] = useState({ name: '', email: '', password: '', confirmPassword: '' })
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [authError, setAuthError] = useState('')
  const [success, setSuccess] = useState(false)
  //Alertbonito
  const toast = useToast()

  function handleChange(field) {
    return (e) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }))
      setErrors((prev) => ({ ...prev, [field]: '' }))
      setAuthError('')
    }
  }

  function validate() {
    const newErrors = {}
    if (!form.name.trim()) newErrors.name = 'El nombre es requerido'
    if (!form.email) newErrors.email = 'El correo es requerido'
    else if (!/\S+@\S+\.\S+/.test(form.email)) newErrors.email = 'Correo no válido'
    const pwdCheck = validatePassword(form.password)
    if (!form.password) newErrors.password = 'La contraseña es requerida'
    else if (!pwdCheck.isValid) newErrors.password = 'La contraseña no cumple los requisitos de seguridad'
    if (!form.confirmPassword) newErrors.confirmPassword = 'Confirma tu contraseña'
    else if (form.password !== form.confirmPassword) newErrors.confirmPassword = 'Las contraseñas no coinciden'
    if (!acceptedTerms) newErrors.acceptedTerms = 'Debes aceptar la Política de Privacidad para continuar'
    return newErrors
  }

  async function handleSubmit(e) {
    e.preventDefault()
    const newErrors = validate()
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return }

    setLoading(true)
    setAuthError('')

const { data, error } = await supabase.auth.signUp({
  email: form.email,
  password: form.password,
  options: {
    data: { full_name: form.name },
  },
})

if (error) {
  setLoading(false)
  if (error.message.includes('already registered')) {
    toast.error('Este correo ya está registrado. Intenta iniciar sesión.')
  } else {
    toast.error(error.message)
  }
  return
}

// 👇 Nuevo: Supabase no manda error cuando el correo ya existe,
// pero devuelve identities vacío como señal
if (data?.user && data.user.identities && data.user.identities.length === 0) {
  setLoading(false)
  toast.error('Este correo ya está registrado. Intenta iniciar sesión.')
  return
}

// Guardamos la evidencia de aceptación de la Política de Privacidad en el perfil
if (data?.user) {
  const { error: profileError } = await supabase
    .from('profiles')
    .update({
      acepto_terminos: true,
      fecha_aceptacion_terminos: new Date().toISOString(),
    })
    .eq('id', data.user.id)

  if (profileError) {
    // No bloqueamos el registro por esto, pero lo dejamos loggeado para revisar
    console.error('Error guardando aceptación de términos:', profileError)
  }
}

setLoading(false)
setSuccess(true)
  }

  if (success) {
    return (
      <AuthLayout>
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          <div style={{ fontSize: 52, marginBottom: 16 }}>📬</div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 600, marginBottom: 10 }}>
            ¡Casi listo!
          </h2>
          <p style={{ fontSize: 14, color: 'var(--color-muted)', lineHeight: 1.6 }}>
            Te enviamos un correo de confirmación a{' '}
            <strong style={{ color: 'var(--color-text)' }}>{form.email}</strong>
          </p>
          <p style={{ fontSize: 13, color: 'var(--color-muted)', marginTop: 10 }}>
            Revisa tu bandeja de entrada y haz clic en el enlace para activar tu cuenta.
          </p>
          <Link to="/login">
            <button style={{
              marginTop: 24, padding: '11px 24px',
              background: 'var(--color-primary)', color: '#fff',
              fontSize: 14, fontWeight: 600, borderRadius: 'var(--radius-sm)',
            }}>
              Ir al inicio de sesión
            </button>
          </Link>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout>
      <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 600, marginBottom: 6 }}>
        Crea tu cuenta
      </h2>
      <p style={{ fontSize: 14, color: 'var(--color-muted)', marginBottom: 20 }}>
        Empieza a controlar tus finanzas hoy
      </p>

      {authError && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fecaca',
          borderRadius: 'var(--radius-sm)', padding: '10px 14px',
          marginBottom: 18, fontSize: 13, color: 'var(--color-danger)',
        }}>
          ⚠️ {authError}
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Input
          label="Nombre completo" type="text"
          value={form.name} onChange={handleChange('name')}
          placeholder="Ana García" error={errors.name}
          required autoComplete="name"
        />
        <Input
          label="Correo electrónico" type="email"
          value={form.email} onChange={handleChange('email')}
          placeholder="tucorreo@ejemplo.com" error={errors.email}
          required autoComplete="email"
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <Input
            label="Contraseña" type="password"
            value={form.password} onChange={handleChange('password')}
            placeholder="Mínimo 8 caracteres" error={errors.password}
            required autoComplete="new-password"
          />
          <PasswordStrength password={form.password} />
        </div>
        <Input
          label="Confirmar contraseña" type="password"
          value={form.confirmPassword} onChange={handleChange('confirmPassword')}
          placeholder="Repite tu contraseña" error={errors.confirmPassword}
          required autoComplete="new-password"
        />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{
            display: 'flex', alignItems: 'flex-start', gap: 8,
            fontSize: 13, color: 'var(--color-muted)', cursor: 'pointer', lineHeight: 1.5,
          }}>
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={(e) => {
                setAcceptedTerms(e.target.checked)
                setErrors((prev) => ({ ...prev, acceptedTerms: '' }))
              }}
              style={{ marginTop: 2, flexShrink: 0 }}
            />
            <span>
              He leído y acepto la{' '}
              <Link to="/privacidad" target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600 }}>
                Política de Privacidad
              </Link>{' '}
              y autorizo el tratamiento de mis datos personales conforme a la Ley 1581 de 2012.
            </span>
          </label>
          {errors.acceptedTerms && (
            <span style={{ fontSize: 12, color: 'var(--color-danger)' }}>
              {errors.acceptedTerms}
            </span>
          )}
        </div>

        <button
          type="submit" disabled={loading}
          style={{
            marginTop: 6, padding: '13px',
            background: loading ? '#86efac' : 'var(--color-primary)',
            color: '#fff', fontSize: 15, fontWeight: 600,
            borderRadius: 'var(--radius-sm)', boxShadow: 'var(--shadow-btn)',
            transition: 'background var(--transition)',
          }}
          onMouseEnter={(e) => { if (!loading) e.target.style.background = 'var(--color-primary-h)' }}
          onMouseLeave={(e) => { if (!loading) e.target.style.background = 'var(--color-primary)' }}
        >
          {loading ? 'Creando cuenta...' : 'Crear cuenta gratis →'}
        </button>
      </form>

      <div style={{
        marginTop: 22, paddingTop: 20,
        borderTop: '1px solid var(--color-border)',
        textAlign: 'center', fontSize: 14, color: 'var(--color-muted)',
      }}>
        ¿Ya tienes cuenta?{' '}
        <Link to="/login" style={{ fontWeight: 600 }}>Inicia sesión</Link>
      </div>
    </AuthLayout>
  )
}